import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import html2canvas from "html2canvas";
import { empty, fromEvent, merge, Subscription } from "rxjs";
import { switchMap, takeUntil, pairwise } from "rxjs/operators";
import * as htmlToImage from "html-to-image";
import { CommonModule } from "@angular/common";

@Component({
  selector: "draw-area",
  templateUrl: "./draw-area.component.html",
  styleUrls: ["./draw-area.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class DrawAreaComponent implements OnDestroy {
  @Output() onChange = new EventEmitter();

  @Input() width: number = 320;
  @Input() height: number = 300;
  @Input() inputDataUrl: string;
  @Input() readOnly: boolean;

  @ViewChild("canvas") public canvas: ElementRef;
  private cx: CanvasRenderingContext2D;
  curPathPoints = [];
  pathHist = [];
  lastPathHist = [];
  bgImg = new Image();
  private drawSub: Subscription;

  public ngAfterViewInit() {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    this.cx = canvasEl.getContext("2d", {
      willReadFrequently: true,
    });

    canvasEl.width = this.width;
    canvasEl.height = this.height;

    this.cx.lineWidth = 2;
    this.cx.lineCap = "round";
    this.cx.strokeStyle = "#000";

    this.loadBg();

    if (!this.readOnly) this.captureEvents(canvasEl);
  }

  loadBg() {
    this.bgImg.onload = () => {
      this.cx.drawImage(this.bgImg, 0, 0, this.width, this.height);
    };
    this.bgImg.src = this.inputDataUrl ?? "assets/img/rx_draw_bg.svg";
  }

  fromEvents(dom, ...eventNames: string[]) {
    return eventNames.reduce(
      (prev, name) => merge(prev, fromEvent(dom, name)),
      empty()
    );
  }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    canvasEl.addEventListener(
      "mouseup",
      () => {
        this.pathHist.push(this.curPathPoints);
        this.curPathPoints = [];
      },
      false
    );

    canvasEl.addEventListener(
      "touchend",
      () => {
        this.pathHist.push(this.curPathPoints);
        this.curPathPoints = [];
      },
      false
    );

    // this will capture all mousedown events from the canvas element
    this.drawSub = this.fromEvents(canvasEl, "mousedown", "touchstart")
      .pipe(
        switchMap((e: any) => {
          e.preventDefault();

          // after a mouse down, we'll record all mouse moves
          return this.fromEvents(canvasEl, "mousemove", "touchmove").pipe(
            // we'll stop (and unsubscribe) once the user releases the mouse
            // this will trigger a 'mouseup' event
            takeUntil(
              this.fromEvents(canvasEl, "mouseup", "touchend", "mouseleave")
            ),
            // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
            //takeUntil(this.fromEvents(canvasEl, "mouseleave")),
            // pairwise lets us get the previous value to draw a line from
            // the previous point to the current point
            pairwise()
          );
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        res[0].preventDefault();
        res[1].preventDefault();

        const rect = canvasEl.getBoundingClientRect();

        // previous and current position with the offset
        const prevPos = {
          x: res[0].pageX - rect.left,
          y: res[0].pageY - rect.top,
        };

        const currentPos = {
          x: res[1].pageX - rect.left,
          y: res[1].pageY - rect.top,
        };

        this.drawOnCanvas(prevPos, currentPos);
      });
  }

  ngOnDestroy() {
    this.drawSub?.unsubscribe();
  }

  private drawOnCanvas(
    prevPos: { x: number; y: number },
    currentPos: { x: number; y: number }
  ) {
    if (!this.cx) {
      return;
    }

    this.cx.beginPath();

    if (prevPos) {
      this.cx.moveTo(prevPos.x, prevPos.y); // from
      this.cx.lineTo(currentPos.x, currentPos.y);
      this.cx.stroke();
    }

    if (!this.curPathPoints.length) {
      this.curPathPoints.push(prevPos);
    }
    this.curPathPoints.push(currentPos);

    this.onChange.emit();
  }

  clear() {
    this.lastPathHist = this.pathHist;
    this.pathHist = [];
    this.cx.clearRect(0, 0, this.width, this.height);

    if (this.inputDataUrl) {
      this.inputDataUrl = null;
      this.loadBg();
    } else {
      this.cx.drawImage(this.bgImg, 0, 0, this.width, this.height);
    }

    this.onChange.emit();
  }

  undo() {
    if (this.pathHist.length) {
      this.cx.clearRect(0, 0, this.width, this.height);
      this.cx.drawImage(this.bgImg, 0, 0, this.width, this.height);

      // remove the last path from the paths array
      this.pathHist.splice(-1, 1);
    } else {
      this.pathHist = this.lastPathHist;
      this.lastPathHist = [];
    }

    // draw all the paths in the paths array
    this.pathHist.forEach((path) => {
      this.cx.beginPath();
      this.cx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) {
        this.cx.lineTo(path[i].x, path[i].y);
      }
      this.cx.stroke();
    });

    this.onChange.emit();
  }

  async toDataUrl(): Promise<string> {
    //let canvas = await html2canvas(this.canvas.nativeElement);
    // return canvas.toDataURL("image/jpeg", 1.0);
    return htmlToImage.toSvg(this.canvas.nativeElement, {
      skipFonts: true,
      pixelRatio: 2,
    });
  }
}
